import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { api, diagnoseNoResponse } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { SocialAuthButtons } from '@/components/auth/SocialAuthButtons';
import { SHOW_DEMO_LOGINS } from '@/lib/demo';
import { clearHistory } from '@/lib/tracking';
import toast from 'react-hot-toast';

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
});

type FormData = z.infer<typeof schema>;

/* Seeded demo accounts - created by backend/prisma/seed.ts. */
const DEMO_ACCOUNTS = [
  { role: 'Buyer',  name: 'Bob Buyer',    email: 'bob@souqli.test',   tone: 'bg-sage' },
  { role: 'Seller', name: 'Alice Seller', email: 'alice@souqli.test', tone: 'bg-gold' },
  { role: 'Admin',  name: 'Souqli Admin', email: 'admin@souqli.test', tone: 'bg-ink' },
] as const;
const DEMO_PASSWORD = 'Demo!Password1';

export default function Login() {
  const { register, handleSubmit, setValue, formState: { errors, isSubmitting } } = useForm<FormData>();
  const setSession = useAuthStore((s) => s.setSession);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [signingInAs, setSigningInAs] = useState<string | null>(null);

  // Password step vs. 2FA code step. Only reached for accounts with 2FA
  // enabled, on a device that hasn't completed a challenge before.
  const [stage, setStage] = useState<'password' | 'code' | 'appeal' | 'appealSent'>('password');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Set when a login attempt succeeds against a suspended/blocked account -
  // the server hands back a narrow, scoped token for filing an appeal
  // instead of a real session (every real session was revoked at suspend/block time).
  const [appealInfo, setAppealInfo] = useState<{ token: string; blocked: boolean; reason?: string } | null>(null);
  const [appealMessage, setAppealMessage] = useState('');
  const [submittingAppeal, setSubmittingAppeal] = useState(false);

  // Always land on the account's own dashboard root - never wherever a
  // previous (possibly different) account's session happened to end.
  // A non-admin whose email is still unverified is routed to the dedicated
  // verification step first; they can't reach the dashboard until it's done.
  function goToDestination(user: any) {
    if (user?.role === 'ADMIN') return navigate('/control-7f3a9b2c');
    if (user?.emailVerified === false) return navigate('/verify-email');
    navigate('/dashboard');
  }

  // A different account may be signing in on this browser without ever
  // hitting the logout button - wipe cached data/recommendations here too so
  // nothing from a previous session leaks into the new one.
  function startSession(user: any, accessToken: string) {
    setSession(user, accessToken);
    queryClient.clear();
    clearHistory();
  }

  async function doLogin(email: string, password: string) {
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.twoFactorRequired) {
        setChallenge(data.challenge);
        setCode('');
        setStage('code');
        return;
      }
      startSession(data.user, data.accessToken);
      goToDestination(data.user);
    } catch (e: any) {
      const code = e?.response?.data?.code;
      if (code === 'ACCOUNT_SUSPENDED' || code === 'ACCOUNT_BLOCKED') {
        setAppealInfo({ token: e.response.data.appealToken, blocked: code === 'ACCOUNT_BLOCKED', reason: e.response.data.reason });
        setAppealMessage('');
        setStage('appeal');
        return;
      }
      throw e;
    }
  }

  async function submitAppeal() {
    if (!appealInfo) return;
    setSubmittingAppeal(true);
    try {
      await api.post('/auth/appeal', { appealToken: appealInfo.token, message: appealMessage });
      setStage('appealSent');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not submit your appeal.', { duration: 9000 });
    } finally {
      setSubmittingAppeal(false);
    }
  }

  async function submitCode() {
    setVerifying(true);
    try {
      const { data } = await api.post('/auth/login/2fa', { challenge, code });
      startSession(data.user, data.accessToken);
      goToDestination(data.user);
    } catch (e: any) {
      const status = e?.response?.status;
      toast.error(
        status === 429 ? 'Too many attempts. Please wait a minute and try again.'
          : e?.response?.data?.message ?? 'Could not verify that code.',
        { duration: 9000 },
      );
    } finally {
      setVerifying(false);
    }
  }

  /**
   * Turn a login failure into the one sentence that actually helps.
   *
   * The server already sends a precise message for anything it can diagnose
   * (bad credentials, database down, schema out of date) - so we prefer that
   * over any guess of our own, and only fall back to a canned line when there
   * is genuinely nothing to go on.
   */
  async function describeLoginError(e: any, demoEmail?: string): Promise<string> {
    if (e?.name === 'ZodError') return 'Enter a valid email and a password of at least 8 characters.';

    // No response at all: down, blocked by CORS, or 500 - find out which.
    if (!e?.response) return diagnoseNoResponse();

    const status = e.response.status;
    const serverMessage = e.response.data?.message as string | undefined;

    // 503 = database unreachable/missing; the API spells out the fix.
    if (status === 503 && serverMessage) return serverMessage;
    if (status === 429) return 'Too many attempts. Please wait a minute and try again.';

    if (status === 401) {
      // For a demo account, 401 means the seed never ran - the account simply
      // isn't in the database. For a real user it means wrong credentials.
      return demoEmail
        ? `The demo account ${demoEmail} doesn’t exist yet. In the backend run: npm run db:bootstrap`
        : 'Email or password is incorrect. If you don’t have an account yet, create one first.';
    }

    return serverMessage ?? `Could not log in (error ${status}).`;
  }

  async function onSubmit(values: FormData) {
    try {
      schema.parse(values);
      await doLogin(values.email, values.password);
    } catch (e: any) {
      toast.error(await describeLoginError(e), { duration: 9000 });
    }
  }

  async function loginAsDemo(email: string) {
    setSigningInAs(email);
    try {
      await doLogin(email, DEMO_PASSWORD);
    } catch (e: any) {
      toast.error(await describeLoginError(e, email), { duration: 9000 });
    } finally {
      setSigningInAs(null);
    }
  }

  function fillDemo(email: string) {
    setValue('email', email);
    setValue('password', DEMO_PASSWORD);
  }

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4 py-12 overflow-hidden">
      {/* Background - a curated rail of pre-loved fashion: what Souqli stands for */}
      <img
        src="https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=1920&h=1280&fit=crop&auto=format&q=80"
        alt="" aria-hidden
        onError={(e) => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1483985988355-763728e1935b?w=1920&h=1280&fit=crop&auto=format&q=80'; }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-ink/90 via-ink/74 to-ink/92" />
      <div className="absolute inset-0 mashrabiya opacity-[0.06] pointer-events-none" />

      <div className="relative z-10 w-full max-w-[400px]">

        {/* Brand + tagline + value props (Facebook-style) */}
        <div className="text-center mb-6">
          <Link to="/" className="display text-[44px] leading-none text-gold tracking-tight drop-shadow">Souqli</Link>
          <p className="text-cream/85 text-[15px] mt-3 leading-snug max-w-[30ch] mx-auto">
            Buy and sell verified pre-loved fashion, tech and more, safely.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-4">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-cream/20 backdrop-blur px-3 py-1.5 text-[12px] text-cream">
              🛍️ Buy quality used items in under <span className="font-semibold text-gold">3&nbsp;min</span>
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-cream/20 backdrop-blur px-3 py-1.5 text-[12px] text-cream">
              🏷️ Sell your used items in under <span className="font-semibold text-gold">5&nbsp;min</span>
            </span>
          </div>
        </div>

        {/* Login card */}
        <div className="bg-white rounded-2xl shadow-frame border border-warmline p-5 sm:p-6">
          {stage === 'password' ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              <div>
                <input
                  className="input input-lg"
                  type="email"
                  autoComplete="email"
                  placeholder="Email address"
                  {...register('email', { required: true })}
                />
                {errors.email && <p className="text-bad text-xs mt-1">Valid email required</p>}
              </div>
              <div>
                <input
                  className="input input-lg"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  {...register('password', { required: true, minLength: 8 })}
                />
                {errors.password && <p className="text-bad text-xs mt-1">At least 8 characters</p>}
              </div>

              <button disabled={isSubmitting} className="btn btn-lg btn-primary w-full !mt-4">
                {isSubmitting ? 'Logging in…' : 'Log in'}
              </button>

              <div className="text-center pt-0.5">
                <Link to="/forgot" className="text-[13px] text-gold-dark hover:underline">Forgotten password?</Link>
              </div>

              <div className="flex items-center gap-3 text-mute my-4">
                <div className="flex-1 h-px bg-warmline" />
                <span className="stamp text-[9px]">or</span>
                <div className="flex-1 h-px bg-warmline" />
              </div>

              <Link to="/register"
                className="btn btn-lg w-full text-center bg-sage-dark text-purewhite border-0 hover:brightness-110 transition">
                Create new account
              </Link>
            </form>
          ) : stage === 'appeal' ? (
            <div className="space-y-3">
              <div className="stamp text-bad text-[10px]">{appealInfo?.blocked ? 'ACCOUNT BLOCKED' : 'ACCOUNT SUSPENDED'}</div>
              <p className="text-[13px] text-ink-700">
                {appealInfo?.blocked
                  ? 'This account has been blocked for a serious violation.'
                  : 'This account has been suspended.'}
                {appealInfo?.reason && <> Reason: <span className="font-medium">{appealInfo.reason}</span>.</>}
              </p>
              <p className="text-[13px] text-mute">You can submit an appeal below - our team will review it.</p>
              <textarea
                value={appealMessage}
                onChange={(e) => setAppealMessage(e.target.value)}
                maxLength={2000}
                placeholder="Explain why you believe this decision should be reconsidered…"
                className="input min-h-[110px]"
              />
              <button
                type="button"
                disabled={appealMessage.trim().length < 10 || submittingAppeal}
                onClick={submitAppeal}
                className="btn btn-lg btn-primary w-full"
              >
                {submittingAppeal ? 'Submitting…' : 'Submit appeal'}
              </button>
              <button
                type="button"
                onClick={() => { setStage('password'); setAppealInfo(null); }}
                className="btn btn-sm btn-ghost text-mute w-full"
              >
                Back
              </button>
            </div>
          ) : stage === 'appealSent' ? (
            <div className="space-y-3 text-center py-4">
              <div className="stamp text-gold-dark text-[10px]">APPEAL SUBMITTED</div>
              <p className="text-[14px] text-ink-700">Thanks - we’ve received your appeal and will review it. We’ll let you know once a decision has been made.</p>
              <Link to="/" className="btn btn-outline w-full !mt-4">Back to Souqli</Link>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="stamp text-gold-dark text-[10px]">TWO-FACTOR VERIFICATION</div>
              <p className="text-[13px] text-ink-700">Enter the 6-digit code from your authenticator app.</p>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                inputMode="numeric"
                autoFocus
                className="input mono w-full text-center tracking-[0.3em] text-lg"
              />
              <button
                type="button"
                disabled={code.length !== 6 || verifying}
                onClick={submitCode}
                className="btn btn-lg btn-primary w-full"
              >
                {verifying ? 'Verifying…' : 'Verify & continue'}
              </button>
              <button
                type="button"
                onClick={() => { setStage('password'); setChallenge(null); setCode(''); }}
                className="btn btn-sm btn-ghost text-mute w-full"
              >
                Back
              </button>
            </div>
          )}

          {/* Social sign-in */}
          {stage === 'password' && (
            <div className="mt-4">
              <SocialAuthButtons
                onAuthed={(user, accessToken) => { startSession(user, accessToken); goToDestination(user); }}
                onTwoFactor={(ch) => { setChallenge(ch); setCode(''); setStage('code'); }}
              />
            </div>
          )}
        </div>

        {/* Demo accounts (dev only) */}
        {SHOW_DEMO_LOGINS && stage === 'password' && (
          <div className="mt-5 card-flat p-4 bg-gold/5 border-gold/30">
            <div className="flex items-center justify-between mb-2.5">
              <div className="stamp text-gold-dark text-[10px]">DEMO ACCOUNTS · QUICK SIGN-IN</div>
              <span className="pill pill-gold text-[9px]">DEV ONLY</span>
            </div>
            <div className="space-y-2">
              {DEMO_ACCOUNTS.map((a) => (
                <div key={a.email} className="flex items-center gap-3 p-2 rounded-luxe bg-white border border-warmline">
                  <div className={`w-8 h-8 rounded-full grid place-items-center text-cream display text-sm ${a.tone}`}>
                    {a.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-medium">{a.name} <span className="stamp text-mute text-[9px] ml-1">{a.role.toUpperCase()}</span></div>
                    <div className="stamp text-mute text-[9px] mono truncate">{a.email}</div>
                  </div>
                  <button type="button" onClick={() => fillDemo(a.email)} className="text-[10px] text-gold-dark hover:underline">Fill</button>
                  <button type="button" onClick={() => loginAsDemo(a.email)} disabled={signingInAs === a.email} className="btn btn-sm btn-gold text-[11px] py-1 px-3">
                    {signingInAs === a.email ? '…' : 'Sign in'}
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2.5 pt-2.5 border-t border-gold/20 flex items-center justify-between text-[10px] text-mute">
              <span>Password (all accounts):</span>
              <code className="mono text-gold-dark">{DEMO_PASSWORD}</code>
            </div>
          </div>
        )}

        <p className="text-center text-[13px] text-cream/70 mt-6">
          Got things to sell? <Link to="/dashboard/sell" className="text-gold font-semibold hover:underline">Start selling on Souqli</Link>.
        </p>
      </div>
    </div>
  );
}
